import type { GenericTableOutput } from 'generated-plugin-api/seeq';
import type { ITEM_TYPES } from '@/trendData/trendData.constants';
import type { UnitOfMeasure } from '@/utilities/unit.utilities';

export interface BoxPlotPoint {
  label?: Highcharts.SVGElement;
  circle?: Highcharts.SVGElement;
  xValue: number;
  yPixel: number;
  color: string;
  labelText: string;
  unitOfMeasure: UnitOfMeasure;
  labelOffset?: number;
  lastX?: number;
}

export type OutlierPoint = {
  value: number;
  timestamp: string;
};

export type OutlierBin = {
  binStart: number;
  binEnd: number;
  values: OutlierPoint[];
};

export type ChartPoint = {
  x: string | number;
  y: number | null;
  color: string;
  marker?: { enabled?: boolean; symbol: string; radius: number; fillColor?: string; rule?: string };
  uom: string;
  timestamp: string;
  itemId: string;
  selected: boolean;
  capsuleIndex?: number; // For Xbar charts - alignment capsule index
};

export interface SpcChartLimits {
  centerLine?: number;
  ucl?: number;
  lcl?: number;
  plus1Sigma?: number;
  minus1Sigma?: number;
  plus2Sigma?: number;
  minus2Sigma?: number;
}

export const I_CHART_KEY = 'I' as const;
export const MR_CHART_KEY = 'MR' as const;
export const XBAR_CHART_KEY = 'XBAR' as const;
export const R_CHART_KEY = 'R' as const;
export const S_CHART_KEY = 'S' as const;

export const primaryCharts = [I_CHART_KEY, XBAR_CHART_KEY];
export const secondaryCharts = [MR_CHART_KEY, R_CHART_KEY, S_CHART_KEY];

export type PrimaryChartKeys = typeof I_CHART_KEY | typeof XBAR_CHART_KEY;
export type SecondaryChartKeys = typeof MR_CHART_KEY | typeof R_CHART_KEY | typeof S_CHART_KEY;

export type ChartKeys = PrimaryChartKeys | SecondaryChartKeys;

export interface SpcChart {
  subgroupSize: number;
  limits: SpcChartLimits;
  rules: string[];
  summary: {
    sampleCount: number;
    ruleViolationCounts: Record<string, number>;
  };
}

export interface StatisticsSummaryItem {
  id: string;
  name: string;
  color: string;
  itemType: ITEM_TYPES.FORMULA_ITEM;
  resultType: string;
  isArchived: boolean;
  additionalProperties?: { name: string; value: string }[];
  error?: {
    message: string;
  };
  warnings?: Array<{
    message: string;
  }>;
  data: {
    inputs: {
      histogramBinWidth: number;
      lowerSpecificationLimit?: number;
      upperSpecificationLimit?: number;
      target?: number;
      intervalStart?: string;
      intervalEnd?: string;
    };
    histogram: [number, number, number][];
    stats: {
      count: number;
      min: number;
      max: number;
      sampleMean: number;
      stdDev: number;
      quartile0: number;
      quartile1: number;
      quartile2: number;
      quartile3: number;
      quartile4: number;
      minWhisker: number;
      maxWhisker: number;
      outliers: OutlierPoint[];
      pp: number;
      ppl: number;
      ppu: number;
      ppk: number;
    };
    uom: string;
    isUncertain: boolean;
    spcCharts?: {
      [key in ChartKeys]?: SpcChart;
    };
  };
  effectivePermissions?: {
    read: boolean;
    write: boolean;
    manage: boolean;
  };
  comparisonData?: {
    inputs: {
      fStatistic: number;
      tStatistic: number;
      meanDifference: number;
      stdDevDifference: number;
      meanPValue: number;
      variancePValue: number;
    };
  };
  tableData?: GenericTableOutput;
}

export enum SPCChartPairType {
  IMR = 'IMR',
  XBarR = 'XBARR',
  XBarS = 'XBARS',
}
